import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { fmtMoney } from './fmtMoney';

function shortKey(lineKey) {
  const key = String(lineKey || '');
  return key.length > 10 ? `…${key.slice(-8)}` : key || '—';
}

function amount(srcLine, field) {
  const amounts = srcLine?.lineLevelTotalAmounts || {};
  return fmtMoney(amounts[field]);
}

function codeLabels(srcLine) {
  const codes = srcLine?.lineAdjudicationCodes || [];
  return codes.map((c) => `${c.type || ''}-${c.code || ''}`).filter((s) => s !== '-');
}

/**
 * Audit breakdown for a submitted line that Optum split into multiple adjudicated
 * service lines (REMARK/Y1). Renders each raw source line and the group totals so
 * the operator can see how the appealable line was reconstructed. Read-only: the
 * appealable unit of selection remains the canonical line in the parent table.
 */
export default function SplitLineDetails({ group }) {
  if (!group) return null;
  const sourceLines = group.sourceLines || [];
  const totals = group.totals || {};
  const hasReference = (group.parentReferenceLineCount || 0) > 0;

  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Optum split this submitted line into {group.sourceLineCount} service line
        {group.sourceLineCount === 1 ? '' : 's'}
        {hasReference
          ? ` (1 reference line excluded from totals to avoid double-counting)`
          : ''}
        .
      </Typography>

      <Table size="small" sx={{ mt: 1, '& td, & th': { fontSize: '0.72rem', borderColor: 'divider' } }}>
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary' } }}>
            <TableCell>Source key</TableCell>
            <TableCell align="right">Units</TableCell>
            <TableCell align="right">Charged</TableCell>
            <TableCell align="right">Allowed</TableCell>
            <TableCell align="right">Paid</TableCell>
            <TableCell>Codes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sourceLines.map((src, idx) => (
            <TableRow key={src.lineKey ? `${src.lineKey}-${idx}` : idx}>
              <TableCell sx={{ fontFamily: 'monospace' }} title={src.lineKey || ''}>
                {shortKey(src.lineKey)}
              </TableCell>
              <TableCell align="right">{src.unitCount ?? '—'}</TableCell>
              <TableCell align="right">{amount(src, 'billedChargeAmount')}</TableCell>
              <TableCell align="right">{amount(src, 'allowedAmount')}</TableCell>
              <TableCell align="right">{amount(src, 'paidAmount')}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>
                {codeLabels(src).join(' ') || '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
        Group totals — Units {group.totalUnits} · Charged {fmtMoney(totals.billedChargeAmount)} ·
        {' '}Allowed {fmtMoney(totals.allowedAmount)} · Paid {fmtMoney(totals.paidAmount)}
      </Typography>
    </Box>
  );
}
